import { FEATURE_GATE_RULE_TYPE, type FeatureGateRuleNode } from './ruleTree';

/**
 * Pure helpers that translate between the form's state (admins rollout
 * %, everyone rollout %, an allow-list, and a deny-list of user ids) and
 * the recursive rule tree stored in `feature_gates.rule_tree`. Kept
 * separate from the form components so the canonicalization rules can be
 * unit-tested in isolation.
 *
 * The "grant" side of a gate is the set of ways a user can be let in:
 * an `is_admin` leaf, an `all_users` leaf, and a `user_allow_list` leaf,
 * combined with `or` (any one grants access). A non-empty deny-list
 * wraps the whole grant in `and(user_deny_list, grant)` so a deny
 * overrides any grant — "deny wins" (the walker's `and` requires every
 * child, and the deny leaf passes only for users NOT on the list).
 *
 * Canonicalization (form → rule):
 *
 * - Grant children, in fixed order, included only when set:
 *   `is_admin@adminPct?`, `all_users@everyonePct?`, `user_allow_list?`.
 * - Zero grant children → `null` (a deny-list alone has nothing to
 *   subtract from; the caller blocks save).
 * - One grant child → that bare leaf. Two+ → `or(children)`.
 * - Non-empty deny-list → `and(user_deny_list, grant)`.
 *
 * Inverse (`ruleToFormState`) recognizes exactly the shapes this emits.
 * Anything else (a hand-edited composition the form can't represent)
 * returns `isCanonical: false` so the edit form can render the
 * non-canonical banner without losing access to the rest of the gate's
 * editable fields.
 */

export interface IFormState {
  /** Integer 0-100. Maps to an `is_admin` leaf when > 0. */
  adminPct: number;
  /** Integer 0-100. Maps to an `all_users` leaf when > 0. */
  everyonePct: number;
  /** Force-pass user ids. Maps to a `user_allow_list` leaf when non-empty. */
  allowUserIds: string[];
  /** Force-fail user ids. Wraps the grant in `and(user_deny_list, …)` when non-empty. */
  denyUserIds: string[];
}

export interface IRuleParseResult extends IFormState {
  /**
   * `true` when the rule tree matches one of the canonical shapes the
   * form emits. `false` means the form cannot represent the rule (e.g. a
   * future leaf type, or a hand-edited non-canonical composition) — the
   * caller should render the banner and fall back to read-only display.
   */
  isCanonical: boolean;
}

const ZERO_FORM_STATE: IFormState = { adminPct: 0, everyonePct: 0, allowUserIds: [], denyUserIds: [] };

/**
 * Builds the canonical rule tree for the supplied form state. Returns
 * `null` when there is no grant (both sliders zero and the allow-list
 * empty) — the caller blocks save in that case rather than emitting a
 * degenerate rule, even when a deny-list is present. Validates the
 * percentages are integers in `[0, 100]`; throws on out-of-range so
 * tests catch programmer error rather than silently clamping.
 */
export function formStateToRule(
  adminPct: number,
  everyonePct: number,
  allowUserIds: string[] = [],
  denyUserIds: string[] = []
): FeatureGateRuleNode | null {
  assertValidPercent(adminPct, 'adminPct');
  assertValidPercent(everyonePct, 'everyonePct');

  const grantChildren: FeatureGateRuleNode[] = [];
  if (adminPct > 0) {
    grantChildren.push({ type: FEATURE_GATE_RULE_TYPE.IS_ADMIN, rolloutPercent: adminPct });
  }
  if (everyonePct > 0) {
    grantChildren.push({ type: FEATURE_GATE_RULE_TYPE.ALL_USERS, rolloutPercent: everyonePct });
  }
  if (allowUserIds.length > 0) {
    grantChildren.push({ type: FEATURE_GATE_RULE_TYPE.USER_ALLOW_LIST, userIds: [...allowUserIds] });
  }

  if (grantChildren.length === 0) {
    return null;
  }

  const grant: FeatureGateRuleNode =
    grantChildren.length === 1 ? grantChildren[0] : { type: FEATURE_GATE_RULE_TYPE.OR, children: grantChildren };

  if (denyUserIds.length > 0) {
    return {
      type: FEATURE_GATE_RULE_TYPE.AND,
      children: [{ type: FEATURE_GATE_RULE_TYPE.USER_DENY_LIST, userIds: [...denyUserIds] }, grant],
    };
  }
  return grant;
}

/** The grant side of a gate, extracted from a bare leaf or an `or` of leaves. */
type IGrant = Pick<IFormState, 'adminPct' | 'everyonePct' | 'allowUserIds'>;

/**
 * Recognizes a grant — a bare `is_admin` / `all_users` /
 * `user_allow_list` leaf, or an `or` of two-or-more such leaves with no
 * repeated type. Returns `null` for anything else (a single-child `or`,
 * a repeated leaf type, a nested operator, a deny leaf) so the caller
 * falls back to the non-canonical banner.
 */
function parseGrant(node: FeatureGateRuleNode): IGrant | null {
  const grant: IGrant = { adminPct: 0, everyonePct: 0, allowUserIds: [] };
  const seen = new Set<string>();

  const consume = (leaf: FeatureGateRuleNode): boolean => {
    if (seen.has(leaf.type)) {
      return false;
    }
    seen.add(leaf.type);
    if (leaf.type === FEATURE_GATE_RULE_TYPE.IS_ADMIN) {
      grant.adminPct = leaf.rolloutPercent;
      return true;
    }
    if (leaf.type === FEATURE_GATE_RULE_TYPE.ALL_USERS) {
      grant.everyonePct = leaf.rolloutPercent;
      return true;
    }
    if (leaf.type === FEATURE_GATE_RULE_TYPE.USER_ALLOW_LIST) {
      grant.allowUserIds = [...leaf.userIds];
      return true;
    }
    return false;
  };

  if (node.type === FEATURE_GATE_RULE_TYPE.OR) {
    // The form emits an `or` only for two-or-more grant children; a
    // single-child `or` is a hand-edited shape it can't round-trip.
    if (node.children.length < 2) {
      return null;
    }
    for (const child of node.children) {
      if (!consume(child)) {
        return null;
      }
    }
    return grant;
  }

  return consume(node) ? grant : null;
}

/**
 * Inverse of `formStateToRule`. Detects the canonical shapes and
 * extracts the form state; returns `isCanonical: false` for any other
 * shape. Order-tolerant on both the `or` grant children and the
 * `and(deny, grant)` wrapper.
 */
export function ruleToFormState(rule: FeatureGateRuleNode): IRuleParseResult {
  if (rule.type === FEATURE_GATE_RULE_TYPE.AND) {
    if (rule.children.length !== 2) {
      return { ...ZERO_FORM_STATE, isCanonical: false };
    }
    const denyNodes = rule.children.filter(
      (c): c is Extract<FeatureGateRuleNode, { type: typeof FEATURE_GATE_RULE_TYPE.USER_DENY_LIST }> =>
        c.type === FEATURE_GATE_RULE_TYPE.USER_DENY_LIST
    );
    const grantNodes = rule.children.filter((c) => c.type !== FEATURE_GATE_RULE_TYPE.USER_DENY_LIST);
    if (denyNodes.length !== 1 || grantNodes.length !== 1) {
      return { ...ZERO_FORM_STATE, isCanonical: false };
    }
    const grant = parseGrant(grantNodes[0]);
    if (!grant) {
      return { ...ZERO_FORM_STATE, isCanonical: false };
    }
    return { ...grant, denyUserIds: [...denyNodes[0].userIds], isCanonical: true };
  }

  const grant = parseGrant(rule);
  if (!grant) {
    return { ...ZERO_FORM_STATE, isCanonical: false };
  }
  return { ...grant, denyUserIds: [], isCanonical: true };
}

/**
 * Renders a one-line human-readable summary for a canonical rule tree.
 * Used by the list view to show e.g. `Admins 100% · Everyone 10% ·
 * Allow 2 · Deny 1` next to the gate name. Returns `Custom rule` for
 * non-canonical trees so the list never crashes on a shape the form
 * can't represent.
 */
export function summarizeRule(rule: FeatureGateRuleNode): string {
  const parsed = ruleToFormState(rule);
  if (!parsed.isCanonical) {
    return 'Custom rule';
  }
  const parts: string[] = [];
  if (parsed.adminPct > 0) {
    parts.push(`Admins ${parsed.adminPct}%`);
  }
  if (parsed.everyonePct > 0) {
    parts.push(`Everyone ${parsed.everyonePct}%`);
  }
  if (parsed.allowUserIds.length > 0) {
    parts.push(`Allow ${parsed.allowUserIds.length}`);
  }
  if (parsed.denyUserIds.length > 0) {
    parts.push(`Deny ${parsed.denyUserIds.length}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'No access';
}

/**
 * Returns the ids present in BOTH lists. A user on the allow-list and
 * the deny-list at once is a contradiction the form forbids: the picker
 * wiring removes a user from one list when they're added to the other,
 * and this is the save-time backstop for a rule hand-edited into that
 * state. Order follows `allowUserIds`.
 */
export function findUserListOverlap(allowUserIds: string[], denyUserIds: string[]): string[] {
  const deny = new Set(denyUserIds);
  return allowUserIds.filter((id) => deny.has(id));
}

function assertValidPercent(pct: number, name: string): void {
  if (!Number.isInteger(pct) || pct < 0 || pct > 100) {
    throw new Error(`${name} must be an integer in [0, 100]; got ${pct}`);
  }
}
